import { COPILOT_ID } from "@repo/app-types/entities/copilot";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { AlpharunMark } from "@repo/ui/chat/alpharunMark";
import {
  ChatHeader,
  chatHeaderIconClassName,
  chatHeaderMarkClassName,
} from "@repo/ui/chat/chatHeader";
import {
  ChatHistory,
  type ChatHistorySection,
} from "@repo/ui/chat/chatHistory";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { IconPlus } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState, type FC } from "react";
import { Button as AriaButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useAllMastraThreadsInfiniteQuery } from "../../api/entities/mastraThread.js";
import {
  FALLBACK_THREAD_TITLE,
  researchAgentThreadMetadataSchema,
  type ResearchAgentThreadMetadata,
} from "./copilotContext.js";
import {
  buildThreadHistorySections,
  getThreadCreatedAt,
} from "./copilotHistorySections.js";

export interface ResearchAgentHistoryViewProps {
  resourceId: string;
  selectedThreadId: string | undefined;
  onThreadSelect: (thread: {
    id: string;
    resourceId: string;
    title: string | undefined;
    metadata: ResearchAgentThreadMetadata;
  }) => void;
  onNewThreadSelect: () => void;
  /** `panel` uses its parent's toolbar; `modalSidebar` has its own header. */
  surface: "panel" | "modalSidebar";
}

/**
 * How long typing settles before the search query is sent. Matches the search
 * modal's feel (`searchQueryDebounceDuration`).
 */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * The research agent's chat history: threads grouped into time buckets, loaded
 * from agent-api with infinite pagination and searched server-side (by title
 * and by message text).
 */
export const ResearchAgentHistoryView: FC<ResearchAgentHistoryViewProps> = (
  props,
) => {
  const isPanelSurface = props.surface === "panel";

  // The input stays responsive while the query it drives lags behind it: every
  // distinct query is its own RTK cache entry, so debouncing keeps a request
  // (and a skeleton) off every keystroke.
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const handle = setTimeout(
      () => setSearchQuery(searchInput.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [searchInput]);

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAllMastraThreadsInfiniteQuery({
    agentId: COPILOT_ID,
    resourceId: props.resourceId,
    query: searchQuery === "" ? undefined : searchQuery,
  });

  const visibleThreads = useMemo(
    () => data?.pages.flatMap((page) => page.threads) ?? [],
    [data?.pages],
  );

  const historySections = useMemo(
    () => buildThreadHistorySections(visibleThreads),
    [visibleThreads],
  );

  const chatHistorySections = useMemo(
    (): ChatHistorySection[] =>
      historySections.map((section) => ({
        id: section.id,
        title: section.title,
        items: section.threads.map((thread) => ({
          id: thread.id,
          title: thread.title || FALLBACK_THREAD_TITLE,
          timestamp:
            getThreadCreatedAt(thread)?.toLocaleString(DateTime.DATETIME_MED) ??
            "now",
        })),
      })),
    [historySections],
  );

  const handleItemSelect = (threadId: string) => {
    const thread = visibleThreads.find(
      (candidate) => candidate.id === threadId,
    );
    if (!thread) {
      return;
    }

    // Skip threads with malformed metadata (e.g. created before the current
    // schema) instead of throwing out of the click handler.
    const metadataParse = researchAgentThreadMetadataSchema.safeParse(
      thread.metadata,
    );
    if (!metadataParse.success) {
      return;
    }

    props.onThreadSelect({
      id: thread.id,
      resourceId: thread.resourceId,
      title: thread.title,
      metadata: metadataParse.data,
    });
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-card">
      {!isPanelSurface ? (
        <ChatHeader
          className="px-4 pt-4 pb-3"
          title="Ask Alpharun"
          titleIcon={<AlpharunMark className={chatHeaderMarkClassName} />}
          actions={
            <IconButton
              accessibleLabel="New chat"
              className="hover:bg-muted"
              variant={ButtonVariant.Tertiary}
              icon={<IconPlus className={chatHeaderIconClassName} />}
              onClick={props.onNewThreadSelect}
            />
          }
        />
      ) : null}

      <div
        className={twMerge(
          "min-h-0 flex-1 overflow-x-hidden overflow-y-auto",
          isPanelSurface ? "px-3 pt-1 pb-3" : "px-4 pt-1 pb-4",
        )}
      >
        {isError ? (
          <p className="py-4 text-center text-sm text-destructive">
            Could not load chats. Please try again.
          </p>
        ) : (
          <ChatHistory
            className="min-h-full"
            sections={chatHistorySections}
            selectedItemId={props.selectedThreadId}
            onItemSelect={handleItemSelect}
            isSearchable
            query={searchInput}
            onQueryChange={setSearchInput}
            isLoading={isLoading}
            emptyState={
              <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
                <p className="text-sm font-medium text-muted-foreground">
                  No chats yet.
                </p>
                <Button
                  variant={ButtonVariant.Secondary}
                  size={Size.SMALL}
                  className="mt-3"
                  onClick={props.onNewThreadSelect}
                >
                  Start chat
                </Button>
              </div>
            }
            footer={
              hasNextPage && !isLoading ? (
                <AriaButton
                  className="py-2 text-center text-sm text-muted-foreground hover:text-foreground"
                  onPress={() => fetchNextPage()}
                  isDisabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? (
                    <Loader
                      className="mx-auto size-4 fill-gray-600"
                      style={LoaderStyle.SPINNER}
                    />
                  ) : (
                    "Load more"
                  )}
                </AriaButton>
              ) : undefined
            }
          />
        )}
      </div>
    </div>
  );
};
